import {
  GoogleSheetsImportJob,
  PurchaseRequestItemState,
  PurchaseRequestItemV2,
  PurchaseRequestStatus,
  PurchaseRequestV2,
} from "../../types";

/**
 * Pure helpers pulled out of the pre-Carbon `PurchaseRequestsV2.tsx` (Task 4 migration) so the
 * request-vs-item derived-status/rollup rules are testable without rendering the screen — same
 * convention `NewRequest/itemStatus.ts`/`defaultOwner.ts` already use for this flow's own logic.
 * Behaviour is copied verbatim; nothing here changes what the pre-Carbon screen computed.
 */

export const getActiveItemsCount = (request: PurchaseRequestV2) =>
  (request.items ?? []).filter((item) => !item.deleted && item.active).length;

export const getAwardedItemsCount = (request: PurchaseRequestV2) =>
  (request.items ?? []).filter(
    (item) => !item.deleted && item.state === PurchaseRequestItemState.AWARDED
  ).length;

/**
 * A request's list-row status is derived from its active items' states rather than shown
 * verbatim from `overallStatus` — mirrors the backend's own `calculateOverallStatus` precedence
 * (delivered > pending-delivery > awarded > partially-awarded > quoting > whatever the backend
 * already computed).
 */
export const getDisplayStatus = (
  request: PurchaseRequestV2
): PurchaseRequestStatus => {
  const activeItems = (request.items ?? []).filter(
    (item) => !item.deleted && item.active
  );
  if (activeItems.length === 0) return request.overallStatus;

  const awardedOrBeyond = (state: PurchaseRequestItemState) =>
    state === PurchaseRequestItemState.AWARDED ||
    state === PurchaseRequestItemState.PENDING_DELIVERY ||
    state === PurchaseRequestItemState.DELIVERED;

  // Only count non-rejected lines when deciding "all awarded" — a rejected line shouldn't block
  // a request whose remaining lines are all awarded.
  const quotableItems = activeItems.filter(
    (item) => item.state !== PurchaseRequestItemState.REJECTED
  );
  const anyAwarded = activeItems.some((item) => awardedOrBeyond(item.state));
  const allAwarded =
    quotableItems.length > 0 &&
    quotableItems.every((item) => awardedOrBeyond(item.state));

  if (allAwarded) {
    // Preserve the more advanced lifecycle states instead of collapsing them to AWARDED.
    if (
      quotableItems.every(
        (item) => item.state === PurchaseRequestItemState.DELIVERED
      )
    ) {
      return PurchaseRequestStatus.DELIVERED;
    }
    if (
      quotableItems.some(
        (item) => item.state === PurchaseRequestItemState.PENDING_DELIVERY
      )
    ) {
      return PurchaseRequestStatus.PENDING_DELIVERY;
    }
    return PurchaseRequestStatus.AWARDED;
  }
  // Some lines awarded while others are still being quoted/approved.
  if (anyAwarded) return PurchaseRequestStatus.PARTIALLY_AWARDED;

  const hasQuoting = activeItems.some(
    (item) => item.state === PurchaseRequestItemState.QUOTING
  );
  if (hasQuoting) return PurchaseRequestStatus.QUOTING;

  return request.overallStatus;
};

/** The earliest value of one date field across a request's active items, or null. */
const getEarliestItemDate = (
  request: PurchaseRequestV2,
  field: "neededFrom" | "neededTo"
): string | null => {
  const dates = (request.items ?? [])
    .filter((item) => !item.deleted && item.active && item[field])
    .map((item) => item[field] as string)
    .sort();
  return dates[0] || null;
};

export const getFirstNeededTo = (request: PurchaseRequestV2): string | null =>
  getEarliestItemDate(request, "neededTo");

/**
 * The date the list shows as "required by": the earliest `neededTo`, else the earliest
 * `neededFrom`. Imported requests usually only carry `neededFrom` (a spreadsheet has one date
 * column, not a window), and showing "-" for all of them hid the one date they do have.
 */
export const getRequiredDate = (request: PurchaseRequestV2): string | null =>
  getFirstNeededTo(request) ?? getEarliestItemDate(request, "neededFrom");

/** The linked user's name, else the ERP code the request came with (SAP requests whose requester
 * has no Simbuy user). */
export const getRequesterName = (request: PurchaseRequestV2): string =>
  request.requester?.fullName || request.requesterCode || "-";

export const getAssignedBuyerNames = (request: PurchaseRequestV2) => {
  const names = (request.items ?? [])
    .map((i) => i.assignedBuyer?.fullName)
    .filter(Boolean) as string[];
  return Array.from(new Set(names));
};

export const getItemRequestId = (item: PurchaseRequestItemV2) =>
  item.request?.id || item.purchaseRequestId || null;

/** Item states that can no longer be pulled into a new order — the items-view bulk-select
 * disables exactly these rows, same set the pre-Carbon `toggleSelectAllItems`/checkbox used. */
export const isItemUnselectable = (item: PurchaseRequestItemV2) =>
  item.state === PurchaseRequestItemState.QUOTING ||
  item.state === PurchaseRequestItemState.AWARDED ||
  item.state === PurchaseRequestItemState.PENDING_DELIVERY ||
  item.state === PurchaseRequestItemState.DELIVERED;

export const getSheetDisplayName = (job: GoogleSheetsImportJob) => {
  if (job.sheetName) return job.sheetName;
  if (job.spreadsheetUrl) {
    const url = job.spreadsheetUrl;
    const match = url.match(/\/d\/([a-zA-Z0-9-_]+)/);
    return match ? `Sheet ${match[1].substring(0, 8)}...` : "Google Sheet";
  }
  return "-";
};
